import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select, Spin, Tooltip } from "antd";
import {
	ArrowUpRight,
	Building2,
	CheckCircle2,
	LifeBuoy,
	RotateCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type {
	ManualFixTicket,
	TicketState,
	WorkspaceTickets,
} from "@/types/technicalAudit";
import {
	useAllWorkspaceTickets,
	useExperts,
	useRecommendationBacklog,
	useSetTicketAssignee,
	useSetTicketState,
} from "@/hooks/useTechnicalAuditIssues";
import { RecommendationBacklogPanel } from "@/components/TechnicalAudit/RecommendationBacklog";

const STATE_META: Record<TicketState, { label: string; cls: string }> = {
	open: { label: "Open", cls: "bg-coral-light text-coral-dark border-coral/40" },
	in_progress: {
		label: "In progress",
		cls: "bg-teal-light text-teal-dark border-teal/40",
	},
	resolved: {
		label: "Resolved",
		cls: "bg-muted text-muted-foreground border-border",
	},
};

type StateFilter = TicketState | "all";

function TicketRow({
	ticket,
	tenantId,
}: {
	ticket: ManualFixTicket;
	tenantId: string;
}) {
	const navigate = useNavigate();
	const setState = useSetTicketState();
	const setAssignee = useSetTicketAssignee();
	const { data: expertsData } = useExperts();
	const experts = expertsData?.experts ?? [];
	const meta = STATE_META[ticket.state];

	return (
		<li className="flex flex-wrap items-center gap-3 px-4 py-2.5">
			<Badge variant="outline" className={cn("shrink-0", meta.cls)}>
				{meta.label}
			</Badge>
			<div className="min-w-0 flex-1">
				<p className="truncate font-mono text-sm text-foreground">
					{ticket.page_url || "(no URL)"}
				</p>
				<p className="truncate text-xs text-muted-foreground">
					{ticket.issue_type}
					{ticket.reason ? ` — ${ticket.reason}` : ""}
				</p>
			</div>
			{/* Assign to an expert (issueId-keyed; works cross-workspace) */}
			<Select
				size="small"
				className="w-40 shrink-0"
				placeholder="Assign expert"
				value={ticket.assignee ?? undefined}
				options={experts.map((e) => ({ value: e.id, label: e.name }))}
				onChange={(expertId) =>
					setAssignee.mutate({ issueId: ticket.issue_id, expertId })
				}
				loading={setAssignee.isPending}
				allowClear={false}
			/>
			{ticket.state !== "resolved" ? (
				<>
					{ticket.state === "open" && (
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="h-7 shrink-0 gap-1 px-2 text-xs"
							disabled={setState.isPending}
							onClick={() =>
								setState.mutate({
									issueId: ticket.issue_id,
									state: "in_progress",
								})
							}
						>
							<RotateCw className="h-3 w-3" />
							Start
						</Button>
					)}
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="h-7 shrink-0 gap-1 px-2 text-xs border-teal/40 text-teal-dark hover:bg-teal-light"
						disabled={setState.isPending}
						onClick={() =>
							setState.mutate({ issueId: ticket.issue_id, state: "resolved" })
						}
					>
						<CheckCircle2 className="h-3 w-3" />
						Resolve
					</Button>
				</>
			) : (
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="h-7 shrink-0 gap-1 px-2 text-xs"
					disabled={setState.isPending}
					onClick={() =>
						setState.mutate({ issueId: ticket.issue_id, state: "open" })
					}
				>
					Reopen
				</Button>
			)}
			<Tooltip title="Open this workspace's Technical Audit tickets board">
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="h-7 shrink-0 gap-1 px-2 text-xs"
					onClick={() =>
						navigate(
							`/audit/technical-issues?run_id=${ticket.run_id ?? ""}&view=tickets&tenant_id=${encodeURIComponent(tenantId)}`,
						)
					}
				>
					<ArrowUpRight className="h-3 w-3" />
					Open
				</Button>
			</Tooltip>
		</li>
	);
}

function WorkspaceSection({
	ws,
	stateFilter,
}: {
	ws: WorkspaceTickets;
	stateFilter: StateFilter;
}) {
	const tickets: ManualFixTicket[] =
		stateFilter === "all"
			? [...ws.open, ...ws.in_progress, ...ws.resolved]
			: ws[stateFilter];
	if (tickets.length === 0) return null;

	return (
		<div className="rounded-xl border bg-card shadow-sm">
			<div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
				<Building2 className="h-4 w-4 shrink-0 text-coral" />
				<span className="font-medium text-foreground">
					{ws.workspace_name || ws.tenant_id}
				</span>
				<span className="text-xs text-muted-foreground">{ws.tenant_id}</span>
				<span className="ml-auto flex gap-2 text-xs">
					<Badge variant="outline" className={STATE_META.open.cls}>
						{ws.counts.open} open
					</Badge>
					<Badge variant="outline" className={STATE_META.in_progress.cls}>
						{ws.counts.in_progress} in progress
					</Badge>
					<Badge variant="outline" className={STATE_META.resolved.cls}>
						{ws.counts.resolved} resolved
					</Badge>
				</span>
			</div>
			<ul className="divide-y">
				{tickets.map((t) => (
					<TicketRow key={t.issue_id} ticket={t} tenantId={ws.tenant_id} />
				))}
			</ul>
		</div>
	);
}

/**
 * Site Health > Tickets — the global ticketing hub, two halves:
 *  - Tickets: every manual-fix ticket in every workspace the user belongs to,
 *    with the full lifecycle inline (assign, start, resolve, reopen — all
 *    issueId-keyed so they work cross-workspace).
 *  - Needs recommendation: every issue from each workspace's latest crawl
 *    that has NO AI recommendation yet — queue generation or escalate it to a
 *    ticket without leaving the page.
 */
export default function CrossWorkspaceTicketsPage() {
	const { data, isLoading, error } = useAllWorkspaceTickets();
	// Always enabled (not view-gated) so the tab badge carries the global count.
	const { data: backlogData } = useRecommendationBacklog();
	const [view, setView] = useState<"tickets" | "backlog">("tickets");
	const [stateFilter, setStateFilter] = useState<StateFilter>("all");
	const [workspaceFilter, setWorkspaceFilter] = useState<string>("all");

	const workspaces = useMemo(() => {
		const all = data?.workspaces ?? [];
		return workspaceFilter === "all"
			? all
			: all.filter((w) => w.tenant_id === workspaceFilter);
	}, [data, workspaceFilter]);

	// Workspace dropdown serves both tabs — union of ticket + backlog tenants.
	const workspaceOptions = useMemo(() => {
		const seen = new Map<string, string>();
		for (const w of data?.workspaces ?? [])
			seen.set(w.tenant_id, w.workspace_name || w.tenant_id);
		for (const w of backlogData?.workspaces ?? [])
			if (!seen.has(w.tenant_id))
				seen.set(w.tenant_id, w.workspace_name || w.tenant_id);
		return [...seen.entries()]
			.sort((a, b) => a[1].localeCompare(b[1]))
			.map(([value, label]) => ({ value, label }));
	}, [data, backlogData]);

	if (isLoading) {
		return (
			<div className="flex h-96 items-center justify-center">
				<Spin size="large" />
			</div>
		);
	}
	if (error) {
		return (
			<div className="p-6 text-sm text-destructive">
				Failed to load tickets across workspaces.
			</div>
		);
	}

	const totals = data?.totals ?? { open: 0, in_progress: 0, resolved: 0 };
	const hasAny =
		totals.open + totals.in_progress + totals.resolved > 0;

	return (
		<div className="mx-auto max-w-5xl space-y-4 p-6">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground">
						<LifeBuoy className="h-6 w-6 text-coral" />
						Tickets
					</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						{view === "tickets"
							? `Expert-fix tickets across every workspace you belong to — ${totals.open} open, ${totals.in_progress} in progress, ${totals.resolved} resolved.`
							: `Issues from each workspace's latest crawl with no AI recommendation yet — ${(backlogData?.totals.issues ?? 0).toLocaleString()} across ${backlogData?.totals.workspaces ?? 0} workspaces.`}
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<div className="flex rounded-md border">
						<button
							type="button"
							className={cn(
								"rounded-l-md px-3 py-1.5 text-sm",
								view === "tickets"
									? "bg-coral text-white"
									: "bg-card text-muted-foreground hover:bg-muted",
							)}
							onClick={() => setView("tickets")}
						>
							Tickets
						</button>
						<button
							type="button"
							className={cn(
								"rounded-r-md border-l px-3 py-1.5 text-sm",
								view === "backlog"
									? "bg-coral text-white"
									: "bg-card text-muted-foreground hover:bg-muted",
							)}
							onClick={() => setView("backlog")}
						>
							Needs recommendation
							{backlogData ? ` (${backlogData.totals.issues.toLocaleString()})` : ""}
						</button>
					</div>
					<Select
						size="middle"
						className="w-52"
						value={workspaceFilter}
						onChange={setWorkspaceFilter}
						options={[{ value: "all", label: "All workspaces" }, ...workspaceOptions]}
					/>
					{view === "tickets" && (
						<Select
							size="middle"
							className="w-36"
							value={stateFilter}
							onChange={(v) => setStateFilter(v as StateFilter)}
							options={[
								{ value: "all", label: "All states" },
								{ value: "open", label: "Open" },
								{ value: "in_progress", label: "In progress" },
								{ value: "resolved", label: "Resolved" },
							]}
						/>
					)}
				</div>
			</div>

			{view === "backlog" ? (
				<RecommendationBacklogPanel workspaceFilter={workspaceFilter} />
			) : !hasAny ? (
				<div className="rounded-xl border bg-card px-8 py-16 text-center">
					<LifeBuoy className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
					<h3 className="text-lg font-semibold text-foreground">
						No tickets anywhere
					</h3>
					<p className="mt-1 text-sm text-muted-foreground">
						Expert-fix tickets filed from any of your workspaces&apos;
						Technical Audits will show up here.
					</p>
				</div>
			) : (
				workspaces.map((ws) => (
					<WorkspaceSection
						key={ws.tenant_id}
						ws={ws}
						stateFilter={stateFilter}
					/>
				))
			)}
		</div>
	);
}
